import { useState } from "react";
import { useForm } from "@inertiajs/react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useBasePath } from "@/lib/paths";
import { FormationSupports } from "@/types";
import { CheckCircle2 } from "lucide-react";
import { LocationSelector } from "@/components/ui/LocationSelector";

type LocationItem = { id: string; code: string; name: string };
type StateItem = LocationItem & { country_id: string };
type LocalAreaItem = LocationItem & { state_id: string };

type Props = {
  formationId: string;
  formationName: string;
  supports: FormationSupports;
  countries: LocationItem[];
  states: StateItem[];
  localAreas: LocalAreaItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  basePath?: string;
};

const ROLE_LABELS: Record<string, string> = {
  owners: "Owner",
  directors: "Director",
  president: "President",
  secretary: "Secretary",
  treasurer: "Treasurer",
  managers: "Manager",
  registered_agent: "Registered Agent",
};

const ROLE_PARAM_KEY: Record<string, string> = {
  owners: "owner",
  directors: "director",
  president: "president",
  secretary: "secretary",
  treasurer: "treasurer",
  managers: "manager",
  registered_agent: "registered_agent",
};

export function AddPrincipalSheet({
  formationId,
  formationName,
  supports,
  countries,
  states,
  localAreas,
  open,
  onOpenChange,
  basePath = "",
}: Props) {
  const defaultBasePath = useBasePath();
  const actualBasePath = basePath || defaultBasePath;
  const [success, setSuccess] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());

  const form = useForm<Record<string, any>>({
    principal_kind: "individual",
    full_name: "",
    legal_name: "",
    email: "",
    phone: "",
    country_code: "",
    state_province: "",
    city: "",
    postal_code: "",
    street_address_1: "",
    street_address_2: "",
    date_of_birth: "",
    tax_id_last4: "",
    roles: {},
  });

  const supportedRoles = Object.entries(supports)
    .filter(([, v]) => v)
    .map(([k]) => k);

  const toggleRole = (roleKey: string) => {
    const next = new Set(selectedRoles);
    const paramKey = ROLE_PARAM_KEY[roleKey];
    if (next.has(roleKey)) {
      next.delete(roleKey);
      const roles = { ...form.data.roles };
      delete roles[paramKey];
      form.setData("roles", roles);
    } else {
      next.add(roleKey);
      const defaults: Record<string, any> = {};
      if (paramKey === "owner") defaults.ownership_percentage = "";
      if (paramKey === "director") defaults.board_chair = "false";
      if (paramKey === "manager") defaults.management_authority = "full";
      form.setData("roles", { ...form.data.roles, [paramKey]: defaults });
    }
    setSelectedRoles(next);
  };

  const updateRoleField = (paramKey: string, field: string, value: string) => {
    form.setData("roles", {
      ...form.data.roles,
      [paramKey]: { ...form.data.roles[paramKey], [field]: value },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    form.post(`${actualBasePath}/formations/${formationId}/principals`, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => {
          setSuccess(false);
          onOpenChange(false);
          form.reset();
          setSelectedRoles(new Set());
        }, 1000);
      },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
      form.clearErrors();
      setSuccess(false);
      setSelectedRoles(new Set());
    }
    onOpenChange(open);
  };

  if (success) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="right" className="sm:max-w-lg">
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm font-medium text-foreground">
              Person added successfully
            </p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Add Person</SheetTitle>
          <SheetDescription>
            Add an owner, director, or officer to {formationName}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-5 px-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">
              Person Type
            </Label>
            <RadioGroup
              value={form.data.principal_kind}
              onValueChange={(value) => form.setData("principal_kind", value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="individual" id="sheet-individual" />
                <Label
                  htmlFor="sheet-individual"
                  className="text-sm font-normal"
                >
                  Individual
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="entity" id="sheet-entity" />
                <Label htmlFor="sheet-entity" className="text-sm font-normal">
                  Entity
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Roles */}
          {supportedRoles.length > 0 && (
            <div>
              <Label className="text-sm font-medium mb-2 block">Roles</Label>
              <div className="space-y-2">
                {supportedRoles.map((roleKey) => (
                  <div key={roleKey}>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`add-role-${roleKey}`}
                        checked={selectedRoles.has(roleKey)}
                        onCheckedChange={() => toggleRole(roleKey)}
                      />
                      <Label
                        htmlFor={`add-role-${roleKey}`}
                        className="text-sm font-normal"
                      >
                        {ROLE_LABELS[roleKey]}
                      </Label>
                    </div>
                    {selectedRoles.has(roleKey) &&
                      ROLE_PARAM_KEY[roleKey] === "owner" && (
                        <div className="ml-6 mt-2 space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Ownership %
                              </Label>
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={
                                  form.data.roles?.owner
                                    ?.ownership_percentage || ""
                                }
                                onChange={(e) =>
                                  updateRoleField(
                                    "owner",
                                    "ownership_percentage",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g. 50"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs text-muted-foreground">
                                Capital ($)
                              </Label>
                              <Input
                                type="number"
                                min="0"
                                value={
                                  form.data.roles?.owner
                                    ?.capital_contribution_cents || ""
                                }
                                onChange={(e) =>
                                  updateRoleField(
                                    "owner",
                                    "capital_contribution_cents",
                                    e.target.value,
                                  )
                                }
                                placeholder="In cents"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    {selectedRoles.has(roleKey) &&
                      ROLE_PARAM_KEY[roleKey] === "director" && (
                        <div className="ml-6 mt-2">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="add-board-chair"
                              checked={
                                form.data.roles?.director?.board_chair ===
                                "true"
                              }
                              onCheckedChange={(checked) =>
                                updateRoleField(
                                  "director",
                                  "board_chair",
                                  checked ? "true" : "false",
                                )
                              }
                            />
                            <Label
                              htmlFor="add-board-chair"
                              className="text-xs font-normal text-muted-foreground"
                            >
                              Board Chair
                            </Label>
                          </div>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium">Name</Label>
            <div className="space-y-1.5">
              <Input
                value={form.data.full_name}
                onChange={(e) => form.setData("full_name", e.target.value)}
                placeholder={
                  form.data.principal_kind === "entity"
                    ? "Entity legal name"
                    : "Full name"
                }
                required
              />
              {form.errors.full_name && (
                <p className="text-[12px] text-destructive">
                  {form.errors.full_name}
                </p>
              )}
            </div>
            {form.data.principal_kind === "entity" && (
              <Input
                value={form.data.legal_name}
                onChange={(e) => form.setData("legal_name", e.target.value)}
                placeholder="DBA name (optional)"
              />
            )}
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Contact</Label>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Input
                  type="email"
                  value={form.data.email}
                  onChange={(e) => form.setData("email", e.target.value)}
                  placeholder="Email"
                />
                {form.errors.email && (
                  <p className="text-[12px] text-destructive">
                    {form.errors.email}
                  </p>
                )}
              </div>
              <Input
                type="tel"
                value={form.data.phone}
                onChange={(e) => form.setData("phone", e.target.value)}
                placeholder="Phone"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium">Address</Label>
            <Input
              value={form.data.street_address_1}
              onChange={(e) => form.setData("street_address_1", e.target.value)}
              placeholder="Street address"
            />
            <Input
              value={form.data.street_address_2}
              onChange={(e) => form.setData("street_address_2", e.target.value)}
              placeholder="Apt, suite, etc. (optional)"
            />
            <LocationSelector
              countries={countries}
              states={states}
              localAreas={localAreas}
              countryCode={form.data.country_code}
              stateProvince={form.data.state_province}
              city={form.data.city}
              onChange={({ country_code, state_province, city }) => {
                form.setData((d: typeof form.data) => ({
                  ...d,
                  country_code,
                  state_province,
                  city,
                }));
              }}
            />
            <Input
              value={form.data.postal_code}
              onChange={(e) => form.setData("postal_code", e.target.value)}
              placeholder="ZIP code"
              className="h-8 text-sm"
            />
          </div>

          {form.data.principal_kind === "individual" && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                Personal Information
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    Date of Birth
                  </Label>
                  <Input
                    type="date"
                    value={form.data.date_of_birth}
                    onChange={(e) =>
                      form.setData("date_of_birth", e.target.value)
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">
                    SSN Last 4
                  </Label>
                  <Input
                    maxLength={4}
                    value={form.data.tax_id_last4}
                    onChange={(e) =>
                      form.setData("tax_id_last4", e.target.value)
                    }
                    placeholder="1234"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-4">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={form.processing}
              >
                {form.processing ? "Adding..." : "Add Person"}
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
